import { LayoutElement, Point, ScaleAxis } from "../types";

/**
 * Returns an axis aligned bounding box for a given element.
 * @param element The element to calculate an AABB for.
 * @returns The AABB for the element.
 */
const getAxisAlignedBoundingBox = (x: number, y: number, width: number, height: number, rotation: number) => {
    const rotationRadians = degreesToRadians(rotation);

    const halfX = width / 2;
    const halfY = height / 2;

    const midX = x + halfX;
    const midY = y + halfY;

    const sin = Math.sin(rotationRadians);
    const cos = Math.cos(rotationRadians);

    const c1x = halfX;
    const c2x = halfX;
    const c1y = -halfY;
    const c2y = halfY;

    const transformedC1X = c1x * cos - c1y * sin;
    const transformedC2X = c2x * cos - c2y * sin;
    const transformC1Y = c1x * sin + c1y * cos;
    const transformedC2Y = c2x * sin + c2y * cos;

    const extentX = Math.max(Math.abs(transformedC1X), Math.abs(transformedC2X));
    const extentY = Math.max(Math.abs(transformC1Y), Math.abs(transformedC2Y));

    return {
        minX: midX - extentX,
        maxX: midX + extentX,
        minY: midY - extentY,
        maxY: midY + extentY,
    };
};

// Returns angle ABC, between lines AB and BC
const findAngle = (a: Point, b: Point, c: Point) => {
    const ab = Math.sqrt(Math.pow(b.x - a.x, 2) + Math.pow(b.y - a.y, 2));
    const bc = Math.sqrt(Math.pow(b.x - c.x, 2) + Math.pow(b.y - c.y, 2));
    const ac = Math.sqrt(Math.pow(c.x - a.x, 2) + Math.pow(c.y - a.y, 2));
    return Math.acos((bc * bc + ab * ab - ac * ac) / (2 * bc * ab)) * (180 / Math.PI);
};

/**
 * Degrees to radians.
 * @param degrees An angle in degrees.
 * @returns The equivelant angle in radians.
 */
const degreesToRadians = (degrees: number) => {
    return degrees * (Math.PI / 180);
};

/**
 * Radians to degrees.
 * @param radians An angle in radians.
 * @returns The equivelant angle in degrees.
 */
const radiansToDegrees = (radians: number) => {
    return radians * (180 / Math.PI);
};

/**
 * @param a The first value.
 * @param b The second value.
 * @returns Returns true if two values are within a distance to each other.
 */
const isCloseToValue = (a: number, b: number, allowableDistance: number) => {
    const diff = Math.abs(a - b);
    return diff < allowableDistance;
};

const getTrueCoordinates = (point: Point, pointOfRotation: Point, rotation: number): Point => {
    const s = Math.sin(degreesToRadians(rotation));
    const c = Math.cos(degreesToRadians(rotation));
    return {
        x: (point.x - pointOfRotation.x) * c - (point.y - pointOfRotation.y) * s + pointOfRotation.x,
        y: (point.x - pointOfRotation.x) * s + (point.y - pointOfRotation.y) * c + pointOfRotation.y,
    };
};

const getPointOfRotation = (nwPoint: Point, sePoint: Point): Point => {
    return {
        x: (nwPoint.x + sePoint.x) / 2,
        y: (nwPoint.y + sePoint.y) / 2,
    };
};

const getNWPoint = (bounds: DOMRect, element: LayoutElement, scaleX: number, scaleY: number): Point => {
    return {
        x: bounds.x + element?.x * scaleX,
        y: bounds.y + element?.y * scaleY,
    };
};

const getNEPoint = (bounds: DOMRect, element: LayoutElement, scaleX: number, scaleY: number): Point => {
    return {
        x: bounds.x + (element?.x + element?.width) * scaleX,
        y: bounds.y + element?.y * scaleY,
    };
};

const getSWPoint = (bounds: DOMRect, element: LayoutElement, scaleX: number, scaleY: number): Point => {
    return {
        x: bounds.x + element?.x * scaleX,
        y: bounds.y + (element?.y + element?.height) * scaleY,
    };
};

const getSEPoint = (bounds: DOMRect, element: LayoutElement, scaleX: number, scaleY: number): Point => {
    return {
        x: bounds.x + (element?.x + element?.width) * scaleX,
        y: bounds.y + (element?.y + element?.height) * scaleY,
    };
};

// Compass directions

const turnRightClockwise = (dir: ScaleAxis): ScaleAxis => {
    switch (dir) {
        case ScaleAxis.North:
            return ScaleAxis.East;
        case ScaleAxis.East:
            return ScaleAxis.South;
        case ScaleAxis.South:
            return ScaleAxis.West;
        case ScaleAxis.West:
            return ScaleAxis.North;
        case ScaleAxis.Northwest:
            return ScaleAxis.Northeast;
        case ScaleAxis.Northeast:
            return ScaleAxis.Southeast;
        case ScaleAxis.Southeast:
            return ScaleAxis.Southwest;
        case ScaleAxis.Southwest:
            return ScaleAxis.Northwest;
    }
};

// The (semi-)cardinal direction that you are currently facing if
// you turned the given rotation from the given orignal direction.
const currentDirection = (originalDirection: ScaleAxis, rotation: number): ScaleAxis => {
    if (rotation > 45 && rotation <= 135) {
        return turnRightClockwise(originalDirection);
    }
    if (rotation > 135 && rotation <= 225) {
        return turnRightClockwise(turnRightClockwise(originalDirection));
    }
    if (rotation > 225 && rotation <= 315) {
        return turnRightClockwise(turnRightClockwise(turnRightClockwise(originalDirection)));
    }
    return originalDirection;
};

/**
 * Computes helpful values about a rotated element, Useful in calculating updated element locations.
 * @param element The element to calculate the vertices for.
 * @returns a, b, c, d starting from top left and moving clockwise. Center represents the point central to
 * all vertices on thge element.
 */
const getElementVertices = (
    element: { x: number; y: number; width: number; height: number; rotation: number },
    canvasBounds: Point = { x: 0, y: 0 },
    scale: Point = { x: 1, y: 1 },
) => {
    const radians = degreesToRadians(element.rotation);

    // Find the unrotated vertices of the element. We'll rotate
    // these later to find the rotated vertices.

    const unrotatedTopLeft: Point = {
        x: canvasBounds.x + element.x * scale.x,
        y: canvasBounds.x + element.y * scale.y,
    };

    const unrotatedTopRight: Point = {
        x: element.x + element.width,
        y: element.y,
    };

    const unrotatedBottomRight: Point = {
        x: canvasBounds.x + (element.x + element.width) * scale.x,
        y: canvasBounds.y + (element.height + element.y) * scale.y,
    };

    const unrotatedBottomLeft: Point = {
        x: canvasBounds.x + element.x * scale.x,
        y: canvasBounds.y + (element.height + element.y) * scale.y,
    };

    // Find the center point of the element.
    // This will be the point that the element rotates on.
    const pointOfRotation: Point = {
        x: (unrotatedBottomLeft.x + unrotatedBottomRight.x) / 2,
        y: unrotatedBottomLeft.y - (element.height * scale.y) / 2,
    };

    // Find the actual coordinates of the element's bottom corners by translating to the origin,
    // rotating as required and translating back to current position.
    return {
        a: rotateAroundPoint(unrotatedTopLeft, pointOfRotation, radians),
        b: rotateAroundPoint(unrotatedTopRight, pointOfRotation, radians),
        c: rotateAroundPoint(unrotatedBottomRight, pointOfRotation, radians),
        d: rotateAroundPoint(unrotatedBottomLeft, pointOfRotation, radians),
        center: pointOfRotation,
    };
};

/**
 * Rotates a point around another point by an angle.
 * @param p The point to rotate.
 * @param c The point to rotate around..
 * @param angleRadians The angle to rotate.
 * @returns A new point transformed by the rotation.
 */
const rotateAroundPoint = (p: Point, c: Point, angleRadians: number) => {
    const sin = Math.sin(angleRadians);
    const cos = Math.cos(angleRadians);
    return {
        x: (p.x - c.x) * cos - (p.y - c.y) * sin + c.x,
        y: (p.x - c.x) * sin + (p.y - c.y) * cos + c.y,
    };
};

export {
    currentDirection,
    degreesToRadians,
    findAngle,
    getAxisAlignedBoundingBox,
    getNEPoint,
    getNWPoint,
    getPointOfRotation,
    getSEPoint,
    getSWPoint,
    getTrueCoordinates,
    isCloseToValue,
    radiansToDegrees,
    turnRightClockwise,
    getElementVertices,
    rotateAroundPoint,
};

// All conversion constants are based on 72dpi
export const mmPerPixel = 0.352778;
export const cmPerPixel = 0.035277;
